import './MaterialDetail.css';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../../auth/useAuth';
import { deleteMaterial, getMaterialBySlug } from '../../services/materialService';
import { formatMaterialValue, formatMaterialComposition } from '../../shared/materialPresentation.mjs';
import MaterialPropertyInsight from './MaterialPropertyInsight';
import ConfirmationModal from '../../components/ConfirmationModal';
import materialDetailInsights from './data/materialDetailInsights';
import { getMaterialMechanicalPropertyPresentation } from './materialMechanicalProperty';
import { getMaterialComparisonUrl } from './materialTools';
import { useUnits } from '../../units/UnitContext';
import { formatUnitValue, unitLabel } from '../../units/unitConversions';

const MaterialDetail = ({ material, onClose, onUpdate, needsEditFetch = false }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { unitSystem } = useUnits();
    const isAdmin = user?.role === "admin";
    const [deleteError, setDeleteError] = useState('');
    const [editError, setEditError] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const handleClose = () => onClose ? onClose() : navigate('/materials/listofmaterials');

    const handleDeleteMaterial = async (materialId) => {
        if (isDeleting) return;
        setIsDeleting(true);
        try {
            setDeleteError('');
            await deleteMaterial(materialId);
            handleClose();
            onUpdate?.();
        } catch (error) {
            console.error('Error deleting material:', error);
            setDeleteError('Unable to delete material. Please try again.');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirmation(false);
        }
    };

    const handleEdit = async () => {
        try {
            setEditError('');
            // The document payload intentionally excludes internal form fields.
            const editableMaterial = needsEditFetch ? await getMaterialBySlug(material.slug) : material;
            navigate('/materials/materialform', { state: { material: editableMaterial } });
        } catch {
            setEditError('Unable to load material for editing. Please try again.');
        }
    };

    const handleDuplicate = async () => {
        try {
            setEditError('');
            const source = needsEditFetch ? await getMaterialBySlug(material.slug) : material;
            navigate('/materials/materialform', { state: { material: source, mode: 'duplicate' } });
        } catch {
            setEditError('Unable to load material for duplication. Please try again.');
        }
    };

    const chemicalComposition = Array.isArray(material.chemicalComposition)
        ? material.chemicalComposition
        : [];

    const otherNames = Array.isArray(material.otherNames)
        ? material.otherNames
        : [];

    const formattedComposition = formatMaterialComposition(chemicalComposition);
    const mechanicalProperty = getMaterialMechanicalPropertyPresentation(material);
    const mechanicalInsight = mechanicalProperty
        ? (mechanicalProperty.scale === 'MPa_Rm'
            ? materialDetailInsights.tensileStrength
            : materialDetailInsights.hardness)
        : materialDetailInsights.mechanicalProperty;

    return (
        <div className="material-detail">
            <div className="material-header">
                <h1>{material.materialName}</h1>
                <div>
                    <Link className="header-button material-compare-action"
                        to={getMaterialComparisonUrl('materialA', material.slug)}>
                        Compare this material
                    </Link>
                    {isAdmin && (
                        <>
                            <button className="header-button" onClick={handleEdit}>Edit</button>
                            <button className="header-button" onClick={handleDuplicate}>Duplicate</button>
                        </>
                    )}
                    <button className="header-button" onClick={handleClose}>Close</button>
                </div>
            </div>
            <div className="material-properties-table">
                {editError && <p role="alert">{editError}</p>}
                <div className="material-properties">
                    <MaterialPropertyInsight
                        insight={mechanicalInsight}
                        value={mechanicalProperty
                            ? mechanicalProperty.scale === 'MPa_Rm'
                                ? formatMaterialValue(formatUnitValue(mechanicalProperty.value, 'mpa', unitSystem), unitLabel('mpa', unitSystem))
                                : formatMaterialValue(mechanicalProperty.value, mechanicalProperty.unit)
                            : 'Not available'}
                    />
                    <MaterialPropertyInsight
                        insight={materialDetailInsights.thermalConductivity}
                        value={formatMaterialValue(material.thermalConductivity, 'W/(m·K)')}
                    />
                    <MaterialPropertyInsight
                        insight={materialDetailInsights.elongation}
                        value={formatMaterialValue(material.elongation, '%')}
                    />
                    <MaterialPropertyInsight
                        insight={materialDetailInsights.chemicalComposition}
                        value={formattedComposition}
                    />
                </div>
                <div className="material-other-names">
                    <strong>Other Names: </strong>
                    <ul>
                        {otherNames.length > 0 ? (
                            otherNames.map((name, index) => <li key={index}>{name}</li> )
                        ) : (
                            <li>None</li>
                        )}
                    </ul>
                </div>
            </div>
            <div>
                <strong>Material Description:</strong>
                <p>{material.materialDescription}</p>
            </div>
            <div>
                <strong>Is Checked: </strong>
                <p>{material.isChecked ? 'Yes' : 'No'}</p>
            </div>
            {isAdmin && (
                <div className="delete-button">
                    {deleteError && <p role="alert">{deleteError}</p>}
                    <button className="header-button" onClick={() => setShowDeleteConfirmation(true)}
                        disabled={isDeleting}>Delete</button>
                </div>
            )}
            <ConfirmationModal
                open={showDeleteConfirmation}
                title={`Move "${material.materialName}" to Trash?`}
                confirmLabel="Move to Trash"
                onCancel={() => setShowDeleteConfirmation(false)}
                onConfirm={() => handleDeleteMaterial(material._id)}
                submitting={isDeleting}
            >
                <p>This material will be hidden immediately and permanently deleted after 7 days.
                    You can restore it from Trash before then.</p>
            </ConfirmationModal>
        </div>
    );
};

export default MaterialDetail;
